import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive touch-manipulation",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 hover:shadow-sm",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border border-interface bg-background shadow-xs hover:bg-interface-light hover:text-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-button text-black shadow-xs hover:bg-action-hover hover:text-black dark:bg-secondary dark:text-black dark:hover:bg-secondary/80",
        ghost:
          "hover:bg-interface-light hover:text-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        success: "bg-aprovar text-white shadow-xs hover:bg-4CAF50",
        warning: "bg-status-pending text-white shadow-xs hover:bg-status-pending/90",
      },
      size: {
        default: "h-11 px-4 py-2 has-[>svg]:px-3", // Increased from h-9 to h-11 (44px)
        sm: "h-10 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5", // Increased from h-8 to h-10 (40px) 
        lg: "h-12 rounded-md px-6 has-[>svg]:px-4", // Increased from h-10 to h-12 (48px)
        icon: "size-11", // Increased from size-9 to size-11 (44px)
        mobile: "h-12 px-6 text-base", // New mobile size for primary actions
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
